import {
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ButtonPrimary } from '@components/button';
import { ButtonCritical } from '@components/button/src/button-critical';
import { FLOORPLAN_LAYER } from '@components/floorplan-v2/floorplan-layers';
import { IconDropper, IconTrash } from '@components/icons';
import { ModalHeader } from '@components/modals/modal-header';
import { ModalV2 } from '@components/modals/modal-v2/src/modal-v2';
import { hexToHsva, type HsvaColor, hsvaToHex, Hue, Saturation } from '@uiw/react-color';
import classNames from 'classnames';

import { ColorPickerPopoverProps } from './color-picker-popover.type';

const isValidHex = (value: string) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value);

const hexDigitsFromHsva = (next: HsvaColor): string => hsvaToHex(next).replace(/^#/, '').toUpperCase();
const DROPPER_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cg transform='rotate(-45 12 12)'%3E%3Cpath d='M15.5 3.5a2.12 2.12 0 0 0-3 3l.5.5L9.5 10.5l-1-1-2 2 1 1-4 4a1.41 1.41 0 0 0 2 2l4-4 1 1 2-2-1-1 3.5-3.5.5.5a2.12 2.12 0 1 0 3-3z' fill='%23222222'/%3E%3Ccircle cx='5.5' cy='18.5' r='2.3' fill='%23ffffff' stroke='%23222222' stroke-width='1.2'/%3E%3C/g%3E%3C/svg%3E") 4 18, crosshair`;

const rgbToHex = (r: number, g: number, b: number): string =>
  `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`.toLowerCase();

const PREVIEW_SIZE = 32;
const PREVIEW_MARGIN = 8;
const PREVIEW_OFFSET_X = 20;
const PREVIEW_OFFSET_Y = 16;
const POPOVER_WIDTH = 288;
const POPOVER_HEIGHT = 320;
const POPOVER_OFFSET = 8;
const VIEWPORT_PADDING = 12;
const PANZOOM_EVENTS = ['panzoomchange', 'panzoompan', 'panzoomzoom'] as const;

const getAncestorElements = (element: HTMLElement | null): HTMLElement[] => {
  if (!element) return [];
  const ancestors: HTMLElement[] = [];
  let parent: HTMLElement | null = element.parentElement;
  while (parent) {
    ancestors.push(parent);
    parent = parent.parentElement;
  }
  return ancestors;
};

const addPanzoomListeners = (
  ancestorElements: HTMLElement[],
  listener: EventListener,
  listenerOptions: AddEventListenerOptions
) => {
  for (const ancestor of ancestorElements) {
    for (const eventName of PANZOOM_EVENTS) {
      ancestor.addEventListener(eventName, listener, listenerOptions);
    }
  }
};

const removePanzoomListeners = (
  ancestorElements: HTMLElement[],
  listener: EventListener,
  listenerOptions: AddEventListenerOptions
) => {
  for (const ancestor of ancestorElements) {
    for (const eventName of PANZOOM_EVENTS) {
      ancestor.removeEventListener(eventName, listener, listenerOptions);
    }
  }
};

export const ColorPickerPopover = ({
  color,
  onChange,
  onPickFromFloorPlan,
  floorplanImageUrl,
  placement = 'bottom',
  isOpen: isOpenProp,
  onOpenChange,
  showTrigger = true,
  zIndex,
  onDelete,
  onDone,
}: ColorPickerPopoverProps) => {
  const { t } = useTranslation();
  const [isFloorplanPickerOpen, setIsFloorplanPickerOpen] = useState(false);
  const isControlled = isOpenProp !== undefined;
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const isOpen = isControlled ? isOpenProp : isOpenInternal;
  const [hsva, setHsva] = useState<HsvaColor>(() => hexToHsva(color || '#000000'));
  const [hexDigits, setHexDigits] = useState(() => hexDigitsFromHsva(hexToHsva(color || '#000000')));
  const [hoveredColor, setHoveredColor] = useState(color.toLowerCase());
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const floorplanImageRef = useRef<HTMLImageElement>(null);
  const samplingCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const wasPopoverOpenRef = useRef(false);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});

  const setIsOpen = useCallback(
    (next: boolean) => {
      if (isControlled) {
        if (typeof onOpenChange === 'function') {
          onOpenChange(next);
        }
      } else {
        setIsOpenInternal(next);
      }
    },
    [isControlled, onOpenChange]
  );

  useEffect(() => {
    if (isValidHex(color)) {
      const nextHsva = hexToHsva(color);
      setHsva(nextHsva);
      setHexDigits(hexDigitsFromHsva(nextHsva));
      setHoveredColor(color.toLowerCase());
    }
  }, [color]);

  useEffect(() => {
    if (isOpen) {
      if (!wasPopoverOpenRef.current) {
        const baseHsva = isValidHex(color) ? hexToHsva(color) : hsva;
        setHexDigits(hexDigitsFromHsva(baseHsva));
      }
      wasPopoverOpenRef.current = true;
    } else {
      wasPopoverOpenRef.current = false;
    }
  }, [isOpen, color, hsva]);

  useEffect(() => {
    if (!isFloorplanPickerOpen) {
      setIsPreviewVisible(false);
      samplingCanvasRef.current = null;
    }
  }, [isFloorplanPickerOpen]);

  const updatePopoverPosition = useCallback(() => {
    if (!anchorRef.current) return;
    const resolvedZIndex = zIndex ?? FLOORPLAN_LAYER.colorPickerPopover;
    const rect = anchorRef.current.getBoundingClientRect();
    const measuredHeight = containerRef.current?.offsetHeight ?? 0;
    const popoverHeight = measuredHeight > 0 ? measuredHeight : POPOVER_HEIGHT;
    const preferredLeft = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
    const maxLeft = Math.max(VIEWPORT_PADDING, globalThis.innerWidth - POPOVER_WIDTH - VIEWPORT_PADDING);
    const clampedLeft = Math.min(Math.max(preferredLeft, VIEWPORT_PADDING), maxLeft);
    const availableAbove = rect.top - VIEWPORT_PADDING;
    const availableBelow = globalThis.innerHeight - rect.bottom - VIEWPORT_PADDING;

    const shouldOpenAbove =
      placement === 'top'
        ? availableAbove >= popoverHeight || availableAbove >= availableBelow
        : availableBelow < popoverHeight && availableAbove > availableBelow;

    const preferredTop = shouldOpenAbove ? rect.top - popoverHeight - POPOVER_OFFSET : rect.bottom + POPOVER_OFFSET;
    const maxTop = Math.max(VIEWPORT_PADDING, globalThis.innerHeight - popoverHeight - VIEWPORT_PADDING);
    const clampedTop = Math.min(Math.max(preferredTop, VIEWPORT_PADDING), maxTop);

    setPopoverStyle((previous) => {
      if (
        previous.position === 'fixed' &&
        previous.zIndex === resolvedZIndex &&
        previous.width === POPOVER_WIDTH &&
        previous.left === clampedLeft &&
        previous.top === clampedTop
      ) {
        return previous;
      }

      return {
        position: 'fixed',
        zIndex: resolvedZIndex,
        width: POPOVER_WIDTH,
        left: clampedLeft,
        top: clampedTop,
      };
    });
  }, [placement, zIndex]);

  useEffect(() => {
    if (!isOpen) return;

    updatePopoverPosition();
    const ancestorElements = getAncestorElements(anchorRef.current);

    let scheduledFrameId: number | null = null;
    const schedulePositionUpdate = () => {
      if (scheduledFrameId !== null) return;
      scheduledFrameId = globalThis.requestAnimationFrame(() => {
        scheduledFrameId = null;
        updatePopoverPosition();
      });
    };

    const listenerOptions: AddEventListenerOptions = { capture: true, passive: true };
    addPanzoomListeners(ancestorElements, schedulePositionUpdate, listenerOptions);

    return () => {
      if (scheduledFrameId !== null) {
        globalThis.cancelAnimationFrame(scheduledFrameId);
      }
      removePanzoomListeners(ancestorElements, schedulePositionUpdate, listenerOptions);
    };
  }, [isOpen, updatePopoverPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutside = (e: Event) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || containerRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const opts = { capture: true };
    document.addEventListener('mousedown', handleOutside, opts);
    document.addEventListener('pointerdown', handleOutside, opts);
    return () => {
      document.removeEventListener('mousedown', handleOutside, opts);
      document.removeEventListener('pointerdown', handleOutside, opts);
    };
  }, [isOpen, setIsOpen]);

  const handleSaturationChange = (newHsva: HsvaColor) => {
    setHsva(newHsva);
    setHexDigits(hexDigitsFromHsva(newHsva));
    onChange(hsvaToHex(newHsva));
  };

  const handleHueChange = ({ h }: { h: number }) => {
    const updated = { ...hsva, h };
    setHsva(updated);
    setHexDigits(hexDigitsFromHsva(updated));
    onChange(hsvaToHex(updated));
  };

  const sanitizeHexDigits = (raw: string) =>
    raw
      .replaceAll(/[^0-9A-Fa-f]/g, '')
      .slice(0, 6)
      .toUpperCase();

  const applyHex = (digits: string) => {
    const next = sanitizeHexDigits(digits);
    const expanded =
      next.length === 3
        ? next
            .split('')
            .map((c) => c + c)
            .join('')
        : next;
    const withHash = `#${expanded}`;
    if (!isValidHex(withHash)) return;
    const newHsva = hexToHsva(withHash);
    setHsva(newHsva);
    onChange(withHash.toUpperCase());
    setHexDigits(hexDigitsFromHsva(newHsva));
  };

  const handleHexDigitsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = sanitizeHexDigits(event.target.value);
    setHexDigits(next);
    if (next.length === 6) {
      applyHex(next);
    }
  };

  const handleHexDigitsKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    applyHex(event.currentTarget.value);
  };

  const getPreviewPosition = (clientX: number, clientY: number) => {
    const preferredX = clientX + PREVIEW_OFFSET_X;
    const preferredY = clientY + PREVIEW_OFFSET_Y;
    const radius = PREVIEW_SIZE / 2;

    return {
      x: Math.min(Math.max(preferredX, PREVIEW_MARGIN + radius), window.innerWidth - PREVIEW_MARGIN - radius),
      y: Math.min(Math.max(preferredY, PREVIEW_MARGIN + radius), window.innerHeight - PREVIEW_MARGIN - radius),
    };
  };

  const sampleColorFromImage = ({ clientX, clientY }: { clientX: number; clientY: number }) => {
    const imageElement = floorplanImageRef.current;
    if (!imageElement) return null;

    const rect = imageElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const isOutsideImageBounds =
      clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom;
    if (isOutsideImageBounds) return null;

    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;

    const canvas = samplingCanvasRef.current;
    if (!canvas) return null;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;

    const sampleX = Math.min(
      Math.floor((relativeX / rect.width) * imageElement.naturalWidth),
      imageElement.naturalWidth - 1
    );
    const sampleY = Math.min(
      Math.floor((relativeY / rect.height) * imageElement.naturalHeight),
      imageElement.naturalHeight - 1
    );

    try {
      const pixel = context.getImageData(sampleX, sampleY, 1, 1).data;
      return rgbToHex(pixel[0], pixel[1], pixel[2]);
    } catch {
      return null;
    }
  };

  const handleFloorplanImageLoad = () => {
    const imageElement = floorplanImageRef.current;
    if (!imageElement || imageElement.naturalWidth <= 0 || imageElement.naturalHeight <= 0) return;

    const canvas = document.createElement('canvas');
    canvas.width = imageElement.naturalWidth;
    canvas.height = imageElement.naturalHeight;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return;

    context.drawImage(imageElement, 0, 0, imageElement.naturalWidth, imageElement.naturalHeight);
    samplingCanvasRef.current = canvas;
  };

  const handleFloorplanMouseMove = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const sampledColor = sampleColorFromImage(event);
    if (!sampledColor) {
      setIsPreviewVisible(false);
      return;
    }

    setIsPreviewVisible(true);
    setHoveredColor(sampledColor);
    setPreviewPosition(getPreviewPosition(event.clientX, event.clientY));
  };

  const handleFloorplanSelectColor = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const sampledColor = sampleColorFromImage(event);
    if (!sampledColor) return;

    const selectedColor = sampledColor.toLowerCase();
    setIsFloorplanPickerOpen(false);
    if (onPickFromFloorPlan) {
      onPickFromFloorPlan(selectedColor);
    } else {
      onChange(selectedColor);
      setIsOpen(false);
    }
  };

  const handleOpenFloorplanPicker = () => {
    if (!floorplanImageUrl) return;
    setIsPreviewVisible(false);
    setIsFloorplanPickerOpen(true);
    setIsOpen(false);
  };

  const popoverContent = (
    <div
      ref={containerRef}
      data-testid='color-picker-popover'
      style={popoverStyle}
      className='w-72 rounded-xl bg-primary shadow-[2px_2px_20px_0_rgba(0,0,0,0.15)] overflow-hidden'
      onPointerDown={(event) => event.stopPropagation()}>
      <div className='relative w-full h-44'>
        <Saturation
          hsva={hsva}
          onChange={handleSaturationChange}
          style={{ width: '100%', height: '100%', borderRadius: 0 }}
        />
      </div>

      <div className='px-4 pt-3 pb-4 flex flex-col gap-3'>
        <div className='relative h-3'>
          <Hue
            hue={hsva.h}
            onChange={handleHueChange}
            style={{ width: '100%', height: '100%', borderRadius: '9999px' }}
          />
        </div>

        <div className='relative rounded-md border border-soft bg-primary'>
          <label
            htmlFor='color-picker-hex-digits'
            className='absolute left-3 top-1 cursor-text text-[10px] font-medium uppercase tracking-wide text-tertiary'>
            {t('floorplanV2:addSeatingColorHexLabel')}
          </label>
          <div className='flex items-center gap-0.5 px-3 pb-1 pt-5'>
            <span className='shrink-0 select-none font-mono text-sm text-primary' aria-hidden='true'>
              #
            </span>
            <input
              id='color-picker-hex-digits'
              data-testid='hex-digits-input'
              type='text'
              name='colorHexDigits'
              inputMode='text'
              autoComplete='off'
              spellCheck={false}
              enterKeyHint='done'
              value={hexDigits}
              onChange={handleHexDigitsChange}
              onKeyDown={handleHexDigitsKeyDown}
              className='min-w-0 flex-1 border-0 bg-transparent font-mono text-sm text-primary outline-none focus:ring-0'
            />
          </div>
        </div>

        {floorplanImageUrl && (
          <button
            type='button'
            onClick={handleOpenFloorplanPicker}
            className='w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-body-s border border-extra-soft bg-secondary text-secondary hover:border-soft'>
            <IconDropper className='w-4 h-4' />
            {t('floorplanV2:addSeatingColorPickFromFloorPlan')}
          </button>
        )}

        {(onDelete || onDone) && (
          <div className='flex items-center gap-3'>
            {onDelete && (
              <ButtonCritical onClick={onDelete} className='flex-1 gap-2' dataTestId='color-picker-delete'>
                <IconTrash className='w-4 h-4' aria-hidden='true' />
                {t('common:delete')}
              </ButtonCritical>
            )}
            {onDone && (
              <ButtonPrimary onClick={onDone} className='flex-1' dataTestId='color-picker-done'>
                {t('common:doneButton')}
              </ButtonPrimary>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div ref={anchorRef} className='relative'>
      {showTrigger && (
        <button
          type='button'
          aria-label={t('floorplanV2:addSeatingColorPickerLabel')}
          aria-expanded={isOpen}
          aria-haspopup='true'
          onClick={() => setIsOpen(!isOpen)}
          className={classNames(
            'w-9 h-9 rounded-full flex items-center justify-center transition-all',
            'focus:outline-none focus:ring-2 focus:ring-offset-1',
            { 'scale-110': isOpen }
          )}
          style={{
            background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
            padding: '7px',
          }}>
          <span
            className='w-full h-full rounded-full bg-primary flex items-center justify-center text-sm font-light text-primary leading-none'
            aria-hidden='true'>
            +
          </span>
        </button>
      )}

      {isOpen && createPortal(popoverContent, document.body)}

      <ModalV2
        isOpen={isFloorplanPickerOpen}
        onClose={() => setIsFloorplanPickerOpen(false)}
        variant='xl'
        zIndex={zIndex ?? FLOORPLAN_LAYER.colorPickerModal}
        enableMultipleModals
        ariaLabel={t('floorplanV2:addSeatingColorPickFromFloorPlan')}>
        <ModalHeader
          title={t('floorplanV2:addSeatingColorSamplingTitle')}
          subtitle={t('floorplanV2:addSeatingColorSamplingSubtitle')}
          handleClose={() => setIsFloorplanPickerOpen(false)}
        />
        <div className='relative px-6 py-6'>
          {floorplanImageUrl ? (
            <div className='relative overflow-auto rounded-lg border border-extra-soft bg-secondary max-h-[70dvh]'>
              <button
                type='button'
                onMouseMove={handleFloorplanMouseMove}
                onMouseLeave={() => setIsPreviewVisible(false)}
                onClick={handleFloorplanSelectColor}
                className='block w-full text-left'
                style={{ cursor: DROPPER_CURSOR }}>
                <img
                  ref={floorplanImageRef}
                  src={floorplanImageUrl}
                  alt={t('floorplanV2:addSeatingColorSamplingImageAlt')}
                  className='w-full h-auto select-none pointer-events-none'
                  crossOrigin='anonymous'
                  draggable={false}
                  onLoad={handleFloorplanImageLoad}
                />
              </button>
            </div>
          ) : null}

          {isPreviewVisible ? (
            <div
              aria-hidden='true'
              className={`fixed ${FLOORPLAN_LAYER.colorPickerPreviewClass} pointer-events-none h-8 w-8 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.2)]`}
              style={{
                left: previewPosition.x,
                top: previewPosition.y,
                backgroundColor: hoveredColor,
                transform: 'translate(-50%, -50%)',
              }}
            />
          ) : null}
        </div>
      </ModalV2>
    </div>
  );
};
